import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Flex, Stack } from "@mantine/core";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { Text } from "#components/atoms/Typographies/Text";
import { Title } from "#components/atoms/Typographies/Title";
import { Company } from "#modules/company/infra/Company.dto";
import { ProcessStepOption } from "#modules/onboardingV2/domain/getProcessStepOptions";
import { type DefaultProcessStepSection } from "#modules/onboardingV2/domain/splitDefaultProcessSteps";
import { AddDefaultProcessStepRow } from "#modules/onboardingV2/view/defaultProcessSteps/AddDefaultProcessStepRow";
import { DefaultProcessStepItem } from "#modules/onboardingV2/view/defaultProcessSteps/DefaultProcessStepItem";
import { Icon } from "#shared/icons/Icon";

type DefaultProcessStep = Company["companySettings"]["defaultProcessSteps"][number];

type DefaultProcessStepsColumnProps = {
  title: string;
  section: DefaultProcessStepSection;
  processSteps: DefaultProcessStep[];
  options: ProcessStepOption[];
  isAddPending: boolean;
  onAddClick: () => void;
  onAdd: (option: ProcessStepOption, onSuccess: () => void) => void;
  onDelete: (processStep: DefaultProcessStep) => void;
};

export const DefaultProcessStepsColumn = ({
  title,
  section,
  processSteps,
  options,
  isAddPending,
  onAddClick,
  onAdd,
  onDelete,
}: DefaultProcessStepsColumnProps) => {
  const { t } = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const shouldScrollToEndRef = useRef(false);

  useLayoutEffect(() => {
    if (!isAdding && !shouldScrollToEndRef.current) {
      return;
    }
    const target = listRef.current?.lastElementChild;
    if (!target) {
      return;
    }
    shouldScrollToEndRef.current = false;
    target.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [isAdding, processSteps.length]);

  const handleCancel = () => {
    setIsAdding(false);
  };

  const handleAddClick = () => {
    onAddClick();
    setIsAdding(true);
  };

  const handleAdd = (option: ProcessStepOption) => {
    onAdd(option, () => {
      shouldScrollToEndRef.current = true;
      setIsAdding(false);
    });
  };

  const processStepIds = useMemo(
    () => processSteps.map((processStep) => processStep.id),
    [processSteps],
  );
  const { setNodeRef, isOver } = useDroppable({ id: section });

  const setListNodeRef = (node: HTMLDivElement | null) => {
    listRef.current = node;
    setNodeRef(node);
  };

  return (
    <Flex
      direction="column"
      style={{
        flex: "1 1 0",
        minWidth: 0,
        minHeight: 0,
        alignSelf: "stretch",
        overflow: "hidden",
        backgroundColor: colors.bg.elevation.primary,
        border: `1px solid ${isOver ? colors.border.info.primary : colors.border.tertiary}`,
        borderRadius: radius.xl,
      }}
    >
      <Stack
        gap={spacing[1]}
        px={spacing[6]}
        pt={spacing[6]}
        pb={spacing[4]}
        style={{ flexShrink: 0 }}
      >
        <Title size="tiny">{title}</Title>
        <Text size="small" color={colors.text.secondaryDefault}>
          {t("onboardingV2.defaultProcessSteps.addedCount", { count: processSteps.length })}
        </Text>
      </Stack>

      <Stack
        ref={setListNodeRef}
        gap={spacing[4]}
        pt={spacing[2]}
        pb={spacing[4]}
        style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
      >
        <SortableContext id={section} items={processStepIds} strategy={verticalListSortingStrategy}>
          {processSteps.map((processStep, index) => (
            <DefaultProcessStepItem
              key={processStep.id}
              processStep={processStep}
              position={index + 1}
              onDelete={() => onDelete(processStep)}
            />
          ))}
        </SortableContext>
        {isAdding ? (
          <AddDefaultProcessStepRow
            position={processSteps.length + 1}
            options={options}
            isPending={isAddPending}
            onCancel={handleCancel}
            onAdd={handleAdd}
          />
        ) : null}
      </Stack>

      <Stack
        gap={spacing[2]}
        px={spacing[6]}
        pt={spacing[4]}
        pb={spacing[6]}
        style={{ flexShrink: 0, backgroundColor: colors.bg.elevation.primary }}
      >
        <Button
          variant="secondary"
          size="md"
          fullWidth
          disabled={isAdding}
          leftSection={<Icon name="plus" size={16} />}
          onClick={handleAddClick}
        >
          {t("onboardingV2.processStepConfiguration.addProcessStep")}
        </Button>
      </Stack>
    </Flex>
  );
};
